/*
 * BLANC SPACE CUSTOM: delay Intercom until the visitor's second full page in
 * the current tab. Support links and Shopify design mode can still load it
 * immediately. The official app embed must remain disabled in settings_data.
 */
(() => {
  const SESSION_KEY = 'blancspace_intercom_first_page_seen';
  const APP_ID = 'o5gs7qrb';
  const PROXY_URL = '/apps/intercom-proxy?load_by=themeExtension&inThemeEditor=false';
  let loadPromise;
  let cartSyncTimer;
  let layeringInstalled = false;

  const setLayeringState = (isOpen) => {
    document.documentElement.dataset.bsIntercomState = isOpen ? 'open' : 'launcher';
  };

  const installLayeringController = () => {
    if (layeringInstalled || typeof window.Intercom !== 'function') return;

    layeringInstalled = true;
    setLayeringState(false);

    window.Intercom('onShow', () => setLayeringState(true));
    window.Intercom('onHide', () => setLayeringState(false));
  };

  const getCookie = (name) => {
    const prefix = `${encodeURIComponent(name)}=`;
    const item = document.cookie.split('; ').find((cookie) => cookie.startsWith(prefix));

    return item ? decodeURIComponent(item.slice(prefix.length)) : null;
  };

  const setCookie = (name, value) => {
    document.cookie = `${encodeURIComponent(name)}=${encodeURIComponent(value)}; path=/; SameSite=Lax`;
  };

  const removeCookie = (name) => {
    document.cookie = `${encodeURIComponent(name)}=; max-age=0; path=/; SameSite=Lax`;
  };

  const waitForIntercom = (attempt = 0) => new Promise((resolve) => {
    if (typeof window.Intercom === 'function' && window.Intercom.booted) {
      resolve(true);
      return;
    }

    if (attempt >= 40) {
      resolve(false);
      return;
    }

    window.setTimeout(() => resolve(waitForIntercom(attempt + 1)), 250);
  });

  const saveCart = async () => {
    const cartId = getCookie('cart');

    if (!cartId || typeof window.Intercom !== 'function') return;

    // Before the real widget boots, window.Intercom is the queue stub and
    // calling it returns the queue length — a truthy number that would sync
    // the cart against a bogus lead id. Only trust a booted widget's answer.
    const rawVisitorId = !window.intercomSettings?.email && window.Intercom.booted
      ? window.Intercom('getVisitorId')
      : null;
    const email = window.intercomSettings?.email;
    const userId = typeof rawVisitorId === 'string' && rawVisitorId ? rawVisitorId : null;
    const markerName = email ? 'last_cart_user' : 'last_cart_lead';

    if ((!email && !userId) || getCookie(markerName) === cartId) return;

    removeCookie(markerName);
    setCookie(markerName, cartId);

    const parameters = new URLSearchParams({ cart_id: cartId });
    parameters.set(email ? 'email' : 'user_id', email || userId);

    try {
      await fetch(`/apps/intercom-proxy/save_cart?${parameters}`, {
        credentials: 'same-origin',
        keepalive: true
      });
    } catch (error) {
      removeCookie(markerName);
    }
  };

  const queueCartSync = () => {
    window.clearTimeout(cartSyncTimer);
    cartSyncTimer = window.setTimeout(saveCart, 500);
  };

  const installProxyWidget = async () => {
    const response = await fetch(PROXY_URL, { credentials: 'same-origin' });

    if (!response.ok) throw new Error(`Intercom proxy returned ${response.status}`);

    const documentFragment = new DOMParser().parseFromString(await response.text(), 'text/html');
    const source = documentFragment.querySelector('#intercom-widget')?.textContent;

    if (!source) throw new Error('Intercom proxy did not return its widget');

    const script = document.createElement('script');
    script.id = 'blancspace-intercom-widget';
    script.textContent = source;
    document.body.append(script);
  };

  const installPublicWidget = () => {
    window.intercomSettings ||= { app_id: APP_ID };

    if (typeof window.Intercom !== 'function') {
      const intercom = (...args) => intercom.q.push(args);
      intercom.q = [];
      window.Intercom = intercom;
    }

    const script = document.createElement('script');
    script.async = true;
    script.src = `https://widget.intercom.io/widget/${APP_ID}`;
    document.head.append(script);
  };

  const load = () => {
    if (loadPromise) return loadPromise;

    loadPromise = installProxyWidget()
      .catch(() => installPublicWidget())
      .then(async () => {
        const isReady = await waitForIntercom();

        if (isReady) installLayeringController();
        queueCartSync();
      });

    return loadPromise;
  };

  const open = async (fallbackUrl) => {
    try {
      await load();

      if (typeof window.Intercom !== 'function') throw new Error('Intercom is unavailable');
      window.Intercom('show');
    } catch (error) {
      if (fallbackUrl) window.location.assign(fallbackUrl);
    }
  };

  window.BlancSpaceIntercom = { load, open };

  document.addEventListener('cart:change', queueCartSync);
  document.addEventListener('cart:refresh', queueCartSync);
  document.addEventListener('click', (event) => {
    if (!event.target.closest?.('a[href="/account/logout"]')) return;
    if (typeof window.Intercom === 'function') window.Intercom('shutdown');
    setLayeringState(false);
  });

  // Record this page view before any early return: a support deep link
  // (#intercom) is still a seen page, so the messenger persists on the
  // visitor's next page instead of silently vanishing until the third.
  let isReturningPage = Boolean(window.Shopify?.designMode);

  try {
    isReturningPage ||= window.sessionStorage.getItem(SESSION_KEY) === 'true';
    window.sessionStorage.setItem(SESSION_KEY, 'true');
  } catch (error) {
    // Storage can be unavailable in privacy modes. Support links still load Intercom on demand.
  }

  if (window.location.hash === '#intercom') {
    open();
    return;
  }

  if (!isReturningPage) return;

  if ('requestIdleCallback' in window) {
    window.requestIdleCallback(load, { timeout: 4000 });
  } else {
    window.setTimeout(load, 1500);
  }
})();
